import React from 'react';
import { ChevronDown, ChevronRight, Mail, Phone } from 'lucide-react';
import CustomerAccountTools, { ProBadge } from './CustomerAccountTools';
import QuickContact from './QuickContact';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const STATUS_COLORS = { 'Livré': 'bg-green-100 text-green-700', 'Expédié': 'bg-blue-100 text-blue-700', 'En cours': 'bg-yellow-100 text-yellow-700' };

const CustomerOrders = ({ orders }) => (orders.length === 0 ? (
  <p className="text-sm text-gray-500">Aucune commande pour ce client.</p>
) : (
  <ul className="divide-y divide-gray-200">
    {orders.map((o) => (
      <li key={o.id} className="py-2 flex items-center justify-between gap-3 text-sm">
        <div className="min-w-0">
          <p className="font-medium text-blue-600">{o.order_number || o.id.slice(0, 8)}</p>
          <p className="text-xs text-gray-500">{fmtDate(o.created_at)} · {o.city || '—'} · {o.items_count} art.</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-medium text-gray-900">{o.total.toFixed(2)} €</p>
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[o.status] || 'bg-gray-100 text-gray-700'}`}>{o.status}</span>
        </div>
      </li>
    ))}
  </ul>
));

const CustomerCard = ({ c, expanded, onToggle, onChanged }) => (
  <article data-testid={`customer-card-${c.email}`} className={`bg-white rounded-lg shadow-sm ${expanded ? 'ring-1 ring-orange-200' : ''}`}>
    <button type="button" onClick={onToggle} className="w-full text-left p-4 flex items-start gap-3" data-testid={`customer-card-toggle-${c.email}`} aria-expanded={expanded}>
      <div className="w-10 h-10 shrink-0 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-sm font-semibold">{(c.first_name || c.email).charAt(0).toUpperCase()}</div>
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-gray-900">{c.first_name} {c.last_name}</p>
          {c.is_admin && <span className="text-[10px] uppercase tracking-wide bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Admin</span>}
          <ProBadge pro={c.pro} />
        </div>
        <p className="text-xs text-gray-600 flex items-center gap-1 break-all"><Mail size={12} className="shrink-0" /> {c.email}</p>
        {c.phone && <p className="text-xs text-gray-400 flex items-center gap-1"><Phone size={12} /> {c.phone}</p>}
        <div className="grid grid-cols-3 gap-2 pt-1 text-xs">
          <div><p className="text-gray-400">Inscrit</p><p className="text-gray-800">{fmtDate(c.created_at)}</p></div>
          <div><p className="text-gray-400">Commandes</p><p className="font-semibold text-gray-900" data-testid={`customer-card-orders-${c.email}`}>{c.orders_count}</p></div>
          <div><p className="text-gray-400">Dépensé</p><p className="font-semibold text-gray-900" data-testid={`customer-card-spent-${c.email}`}>{c.total_spent.toFixed(2)} €</p></div>
        </div>
      </div>
      <span className="text-gray-400 shrink-0 mt-1">{expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
    </button>
    <QuickContact phone={c.phone} email={c.email} testId={`customer-card-${c.email}`} className="px-4 pb-3 -mt-1" />
    {expanded && (
      <div className="border-t bg-gray-50 px-4 py-3 rounded-b-lg" data-testid={`customer-card-panel-${c.email}`}>
        <CustomerAccountTools customer={c} onChanged={onChanged} />
        <CustomerOrders orders={c.orders} />
      </div>
    )}
  </article>
);

const AdminCustomerCards = ({ customers, expanded, onToggle, onChanged }) => (
  <div className="space-y-3" data-testid="customers-cards">
    {customers.map((c) => <CustomerCard key={c.id} c={c} expanded={expanded === c.id} onToggle={() => onToggle(c.id)} onChanged={onChanged} />)}
  </div>
);

export default AdminCustomerCards;
